import { useState, useContext } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  Tooltip,
  MenuItem
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LoginIcon from '@mui/icons-material/Login';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

import { UserContext } from '../context/UserContext';
import DarkModeSelector from '../components/dark-mode-selector/DarkModeSelector';
import lightLogo from '../logo_light.svg';
import darkLogo from '../logo_dark.svg';

const pages_logged_out = [
  { label: 'Pricing', path: '/pricing', icon: <AttachMoneyIcon /> },
  { label: 'Contact Us', path: '/contact-us', icon: <AccountCircleIcon /> },
];

const settings_logged_out = [
  { label: 'Sign Up', path: '/signup', icon: <PersonAddIcon /> },
  { label: 'Sign In', path: '/signin', icon: <LoginIcon /> },
];

const pages_logged_in = [
  { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
  { label: 'Projects', path: '/project', icon: <AccountTreeIcon /> },
  //{ label: 'Contact Us', path: '/contact-us', icon: <AccountCircleIcon /> },
];

const settings_logged_in = [
  { label: 'Profile', path: '/profile', icon: <AccountCircleIcon /> },
  { label: 'Sign Out', path: '/signout', icon: <LogoutIcon /> },
];

function Navigation({ children, mode, setMode }) {
  const theme = useTheme();
  const location = useLocation();
  const [anchorElUser, setAnchorElUser] = useState(null);

  const { user } = useContext(UserContext);
  const logo = theme.palette.mode === 'dark' ? darkLogo : lightLogo;
  const isLoggedIn = Boolean(user && user.uid);
  //if user role consumer filter out dashboard
  const pages = isLoggedIn ? user?.role === 'producer' ? pages_logged_in : pages_logged_in?.filter(page => page.label !== 'Dashboard') : pages_logged_out;
  const settings = isLoggedIn ? settings_logged_in : settings_logged_out;

  const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);
  const handleCloseUserMenu = () => setAnchorElUser(null);

  return (
    <Box>
      <AppBar position="static" sx={{ display: { xs: 'none', md: 'block' } }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            <Box
              component={RouterLink}
              to="/"
              sx={{ flexGrow: { xs: 1, md: 0 }, display: 'flex', alignItems: 'center', textDecoration: 'none', justifyContent: 'center' }}
            >
              <Box
                component="img"
                src={logo}
                alt="Logo"
                sx={{ height: 60, p: '8px' }}
              />
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
              {pages.map(({ label, path }) => (
                <Button
                  key={label}
                  component={RouterLink}
                  to={path}
                  sx={{ my: 2, color: 'white', display: 'block' }}
                >
                  {label}
                </Button>
              ))}
            </Box>
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end', alignItems: 'center' }}>
              <DarkModeSelector mode={mode} setMode={setMode} />
              <Tooltip title="Open settings">
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleOpenUserMenu}
                  color="inherit"
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                keepMounted
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                {settings.map(({ label, path, icon }) => (
                  <MenuItem
                    key={label}
                    component={RouterLink}
                    to={path}
                    onClick={handleCloseUserMenu}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      {icon}
                      <Typography textAlign="center" sx={{ ml: 1 }}>
                        {label}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Box>{children}</Box>

      <AppBar position="fixed" sx={{ top: 'auto', bottom: 0 }}>
        <Container maxWidth="xl">
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' }, justifyContent: 'center' }}>
            {[...pages, ...settings].map(({ label, path, icon }) => {
              const isActive = location.pathname === path || location.pathname.startsWith(path + '/');
              return (
                <Button
                  key={label}
                  component={RouterLink}
                  to={path}
                  sx={{
                    my: 1,
                    color: isActive ? 'secondary.main' : 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minWidth: 80,
                    px: 1,
                    borderTop: isActive ? 2 : 0,
                    borderColor: 'secondary.main',
                    borderRadius: 0,
                  }}
                >
                  {icon}
                  <Typography variant="caption" sx={{ mt: 0.5 }}>
                    {label}
                  </Typography>
                </Button>
              );
            })}
          </Box>
        </Container>
      </AppBar>
    </Box>
  );
}

export default Navigation;
